/**
 * Public, runtime-first signal contract. Authoring signals (config/content
 * updates, refresh) are intentionally excluded — they translate to these facts
 * at the boundary. `direction` is relative to the widget: `in` = received,
 * `out` = reported.
 */
export type SignalDirection = 'in' | 'out'

export interface SignalDef<Payload = unknown> {
  readonly name: string
  readonly direction: SignalDirection
  /** Phantom carrier for the payload type — never read at runtime. */
  readonly __payload?: Payload
}

const def = <Payload>(name: string, direction: SignalDirection): SignalDef<Payload> => ({ name, direction })

/** Standard runtime signals. */
export const RuntimeSignals = {
  /** Props were replaced/merged. Also fires during edit-mode preview — the widget can't tell. */
  propsChanged: def<Record<string, unknown>>('propsChanged', 'in'),
  /** Design tokens / theme changed. */
  themeChanged: def<Record<string, string>>('theme.changed', 'in'),
  /** The widget's external content source changed (e.g. a new `widgetRef`). */
  contentChanged: def<{ widgetRef?: string }>('content.changed', 'in'),

  /** The widget finished its first meaningful render. */
  ready: def<undefined>('ready', 'out'),
  /** The widget failed to render. */
  renderFailed: def<{ message: string }>('render.failed', 'out'),
  /** The widget's measured size changed. */
  resized: def<{ width: number; height: number }>('resize', 'out')
} as const

export type RuntimeSignal = (typeof RuntimeSignals)[keyof typeof RuntimeSignals]
export type RuntimeSignalName = RuntimeSignal['name']

/** Infer a signal's payload type from its definition. */
export type PayloadOf<S extends SignalDef> = S extends SignalDef<infer P> ? P : never

/** The typed signal layer exposed as `sdk.signals`. */
export interface WidgetSignals {
  /** Subscribe to an inbound signal. Returns an unsubscribe function. */
  on: <S extends SignalDef>(signal: S, handler: (payload: PayloadOf<S>) => void) => () => void
  /** Report an outbound fact to the host. */
  report: <S extends SignalDef>(signal: S, ...payload: PayloadOf<S> extends undefined ? [] : [PayloadOf<S>]) => void
}

/** DOM event name used to surface a widget's outbound reports to the host (in-page transport). */
export const WIDGET_SIGNAL_EVENT = 'gs-cc-widget-signal'

/** `detail` shape of {@link WIDGET_SIGNAL_EVENT}. */
export interface WidgetSignalEventDetail {
  instanceId?: string
  name: string
  payload: unknown
}
